/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useRef } from 'react';

import { throttle } from 'lodash';

import { Hidden, breakpoints } from '@coursera/cds-core';

import useIsTablet from 'bundles/unified-common/hooks/useIsTablet';
import PageNavigationBase from 'bundles/unified-description-page-common/components/shared/PageNavigation';
import xdpStyles from 'bundles/unified-description-page-common/constants/styles';
import { usePageNavigation } from 'bundles/unified-description-page-common/contexts/PageNavigationContext';
import { useXDPRedesignPhase1Data } from 'bundles/unified-description-page-common/providers/specialization/XDPRedesignPhase1Provider';

const styles = {
  container: (enableXDPRedesign: boolean) => css`
    ${breakpoints.up('md')} {
      ${enableXDPRedesign ? xdpStyles.border('border-bottom') : 'border-bottom: 2px solid var(--cds-color-grey-50);'}
    }
  `,
};

const PageNavigation = ({ ...rest }) => {
  const navRef = useRef<HTMLDivElement | null>(null);
  const previousScrollTop = useRef<number>(0);
  const { updateInPageNavPosition } = usePageNavigation();
  const isTablet = useIsTablet();
  const { enableXDPRedesignPhase1, enableXDPRedesignDescriptionRework } = useXDPRedesignPhase1Data();
  const enableXDPRedesign = enableXDPRedesignPhase1 || enableXDPRedesignDescriptionRework;

  useEffect(() => {
    const updateNavPosition = throttle(() => {
      const top = navRef.current ? navRef.current.getBoundingClientRect().top - navRef.current.offsetHeight : undefined;
      if (top !== undefined && updateInPageNavPosition) {
        if (isTablet) {
          const scrollY = window?.scrollY || 0;
          const isScrollingDown = scrollY >= previousScrollTop.current;
          previousScrollTop.current = scrollY;
          updateInPageNavPosition(top, isScrollingDown);
        } else {
          updateInPageNavPosition(top);
        }
      }
    }, 250);

    updateNavPosition();

    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', updateNavPosition);
      window.addEventListener('resize', updateNavPosition);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('scroll', updateNavPosition);
        window.removeEventListener('resize', updateNavPosition);
      }
    };
  }, [updateInPageNavPosition, isTablet]);

  // This gets used as a reference for the fixed page navigation.
  // To remove this would require a large functional change to the provider.
  if (enableXDPRedesign || rest.disablePageNavigationDisplay) {
    return <div ref={navRef} />;
  }

  return (
    <div ref={navRef} css={styles.container(enableXDPRedesign)}>
      <Hidden smDown>
        <PageNavigationBase {...rest} />
      </Hidden>
    </div>
  );
};

export default PageNavigation;
